import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { RouterService } from './router.service';
import { catchError, Observable, of, switchMap } from 'rxjs';
import { CachedHttpService } from './cached-http.service';
import {
    Notification,
    PagedNotifications,
} from '../types/notifications/Notification';
import { Comment } from '../types/notifications/Comment';

@Injectable({ providedIn: 'root' })
export class NotificationService {
    constructor(
        private http: HttpClient,
        private cachedHttp: CachedHttpService,
        private routerService: RouterService,
    ) {}

    /**
     * Gets the notifications api url with the current session.
     */
    get url() {
        return this.routerService.getApiUrl('notifications');
    }

    /**
     * Gets the paged, unseen notifications and caches them.
     * @param skip the number to skip. (default: 0)
     * @param take the number to take. (default: 10)
     * @param reset should bypass cache?
     * @param filter optional notification filter.
     */
    getNotifications(
        skip?: number,
        take?: number,
        reset = false,
        filter?: string,
    ): Observable<PagedNotifications> {
        let url = `${this.url}/${Endpoints.User}`;
        if (filter) {
            url += `?filter=${filter}`;
        }
        const body: any = {};
        if (skip !== undefined) {
            body.skip = skip;
        }
        if (take !== undefined) {
            body.take = take;
        }
        return this.cachedHttp.post<PagedNotifications>(url, body, url, reset);
    }

    getUnreadNotifications(reset = false): Observable<PagedNotifications> {
        return this.getNotifications(undefined, undefined, reset, 'unread');
    }

    /**
     * Requests the api mark all given notifications as seen.
     * Reloads the first page of `take` notifications from the api.
     * @param ids ids of the notifications to archive.
     * @param take the number to take when reloading notifications. (default: 10)
     */
    archiveNotifications(
        ids: number[],
        take = 10,
    ): Observable<PagedNotifications> {
        const skip = 0;
        const url = `${this.url}/${Endpoints.Archive}`;
        return this.http
            .post<unknown>(url, ids)
            .pipe(switchMap((_) => this.getNotifications(skip, take, true)));
    }

    /**
     * Gets a bill's paged activity.
     * @param billId The id of the bill.
     * @param skip Number to skip. (default: 0)
     * @param take Number to take. (default: 50)
     */
    getActivity(billId: string, skip = 0, take = 50) {
        const body = { billId: billId, skip: skip, take: take };
        return this.cachedHttp
            .post<Notification[]>(`${this.url}/${Endpoints.Activity}`, body)
            .pipe(catchError(this.handleError));
    }

    /**
     * Gets a bill's paged notes.
     * @param billId The id of the bill.
     * @param skip Number to skip. (default: 0)
     * @param take Number to take. (default: 50)
     */
    getBillNotes(billId: string, skip = 0, take = 50): Observable<Comment[]> {
        // TODO: Remove this and the params??
        const body = { billId: billId, skip: skip, take: take };
        return this.http
            .get<
                Comment[]
            >(`${this.url}/Bill/${billId}/${Endpoints.Notes}/${take}/${skip}`)
            .pipe(catchError(this.handleError));
    }

    /**
     * Loads all notes for a bill request.
     * @param request The request details.
     */
    getNotes(request: any): Observable<Comment[]> {
        return this.http
            .post<Comment[]>(`${this.url}/${Endpoints.Notes}`, request)
            .pipe(catchError(this.handleError));
    }

    /**
     * Adds a new note.
     * @param note The new note.
     */
    postNote(note: Comment) {
        return this.http
            .post<Object[]>(`${this.url}/${Endpoints.New}`, note)
            .pipe(catchError(this.handleError));
    }

    /**
     * Deletes a note by id.
     * @param id The id of the note.
     */
    deleteNote(id: number) {
        return this.http
            .post<Object[]>(`${this.url}/${Endpoints.Delete}/${id}`, {})
            .pipe(catchError(this.handleError));
    }

    private handleError(error: HttpErrorResponse): Observable<any[]> {
        console.error(error);
        return of([]);
    }
}

enum Endpoints {
    Archive = 'user/archive',
    Activity = 'activity',
    Delete = 'notes/delete',
    New = 'notes/new',
    Notes = 'notes',
    User = 'user',
}
